<template>
  <div class="sign-up-manager-container">
    <ErrorDialogV2
      :value="showPendingUsersWarning"
      @input="showPendingUsersWarning = $event"
      title="Can't turn off approval queue"
      message="There are users waiting in the approval queue. Review them in People and Groups before disabling this setting."
      confirm-button-text="OK"
      :max-width="450"
    />

    <Typography variant="h-4" class="pb-6">Approval</Typography>
    <v-checkbox v-model="approvalRequired" color="brandPrimary" hide-details class="pb-6">
      <template v-slot:label>
        <div class="d-flex flex-column">
          <Typography class="black--text" variant="subtitle-1">
            <strong>Enable Approval Queue</strong>
          </Typography>
          <Typography variant="caption-2"
            >New sign-ups will appear in the Pending tab in People & Groups. Approve them to grant
            access.</Typography
          >
        </div>
      </template>
    </v-checkbox>
    <v-divider />
    <Typography variant="h-4" class="pb-4">Form</Typography>
    <div class="required-fields">
      <Typography variant="subtitle-1">Required by Default</Typography>
      <div class="required-fields-list">
        <Typography variant="body-1"> First Name </Typography>
        <Typography variant="body-1"> Last Name </Typography>
        <Typography variant="body-1">
          {{ isOne10PointsEnabled(client && client.clientId) ? "Email" : "Email or Phone" }}
        </Typography>
      </div>
    </div>
    <div class="additional-fields pb-6">
      <Typography variant="subtitle-1">Additional Fields</Typography>
      <Typography variant="body-2" class="pb-6 pt-2">
        To add more required fields, select from the available optional fields.
      </Typography>
      <div
        class="additional-fields-list-item"
        v-for="(_item, index) in selectedAdditionalFields"
        :key="index"
      >
        <v-select
          :items="items"
          outlined
          hide-details="auto"
          label="Additional Field"
          v-model="selectedAdditionalFields[index]"
          :error="isDuplicate(index)"
          :error-messages="isDuplicate(index) ? 'Duplicate field' : ''"
        />
        <Button
          plain
          :class="[isDuplicate(index) ? 'trash-button-padding-bottom' : '']"
          @click="removeAdditionalField(index)"
        >
          <v-icon>mdi-trash-can</v-icon>
        </Button>
      </div>
    </div>
    <Button
      iconLeft="mdi-plus"
      class="align-self-start"
      @click="addAdditionalField"
      plain
      :disabled="selectedAdditionalFields.length >= items.length"
    >
      Add
    </Button>
    <Button
      class="align-self-center"
      @click="handleSave"
      :disabled="!isFormValid"
      :loading="loading"
      >Save Changes</Button
    >
  </div>
</template>

<script>
import Typography from "@/components/generic/Typography.vue";
import Button from "@/components/generic/Button.vue";
import ErrorDialogV2 from "@/components/ErrorDialogV2.vue";
import ClientService from "@/services/ClientServiceV2";
import { mapState } from "vuex";

import isOne10PointsEnabled from "@/mixins/one10Points/isOne10PointsEnabled";
export default {
  name: "SignUpManager",
  mixins: [isOne10PointsEnabled],
  components: {
    Typography,
    Button,
    ErrorDialogV2,
  },
  props: {
    client: {
      type: Object,
      required: false,
    },
  },
  data() {
    return {
      clientService: new ClientService(),
      approvalRequired: false,
      originalApprovalRequired: false,
      items: [
        { text: "Job Title", value: "jobTitle" },
        { text: "Organization", value: "organization" },
      ],
      selectedAdditionalFields: [],
      claimRegistrationFormFields: this.client?.ClientRegistrationFormFields || [],
      loading: false,
      dropdownItemsMap: [],
      showPendingUsersWarning: false,
    };
  },
  computed: {
    ...mapState(["pendingUserCount"]),
    isFormValid() {
      if (this.selectedAdditionalFields.length === 0) {
        return true;
      }
      return (
        this.selectedAdditionalFields.every(
          (field) => typeof field === "string" && field.trim() !== ""
        ) && new Set(this.selectedAdditionalFields).size === this.selectedAdditionalFields.length
      );
    },
  },
  watch: {},
  methods: {
    removeAdditionalField(index) {
      this.selectedAdditionalFields.splice(index, 1);
    },
    addAdditionalField() {
      this.selectedAdditionalFields.push("");
    },
    isDuplicate(index) {
      const value = this.selectedAdditionalFields[index];
      if (!value) return false;
      return this.selectedAdditionalFields.slice(0, index).includes(value);
    },
    async handleSave() {
      try {
        this.loading = true;

        // Check if approval queue is being disabled
        if (this.originalApprovalRequired && !this.approvalRequired) {
          // If pending user count is null, fetch it first
          if (this.pendingUserCount?.count === null) {
            await this.$store.dispatch("fetchPendingUserCount");
          }

          const count = this.pendingUserCount?.count ?? 0;
          if (count > 0) {
            this.showPendingUsersWarning = true;
            this.loading = false;
            this.approvalRequired = true;
            return;
          }
        }

        const formFieldsPayload = this.selectedAdditionalFields.map((field, index) => {
          return {
            userAttribute: field,
            dropdownOptions: this.dropdownItemsMap.find((item) => item.value === field)
              ?.dropdownOptions,
            status: "Active",
            order: index + 1,
          };
        });

        console.log("Saving with payload:", {
          clientId: this.client.clientId,
          formFields: formFieldsPayload,
          approvalRequired: this.approvalRequired,
        });

        await Promise.all([
          this.clientService.updateClientRegistrationFormFieldsV3(
            this.client.clientId,
            formFieldsPayload
          ),
          this.clientService.updateClientV3(this.client.clientId, {
            onboardingApprovalQueueEnabled: this.approvalRequired,
          }),
        ]);

        // Update original value after successful save
        this.originalApprovalRequired = this.approvalRequired;
        this.$emit("getClient", { clientId: this.client.clientId });
      } catch (error) {
        console.error("Failed to save client registration form fields", error);
        console.error("Error details:", error.response?.data);
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.approvalRequired = this.client?.onboardingApprovalQueueEnabled || false;
    this.originalApprovalRequired = this.approvalRequired;

    this.selectedAdditionalFields =
      this.client?.ClientRegistrationFormFields?.filter((field) => field.status === "Active").map(
        (field) => field.userAttribute
      ) || [];

    // Saving dropdown options for currently active fields so they don't get overridden when the user saves the form
    for (const field of this.client?.ClientRegistrationFormFields || []) {
      if (field.status === "Active" && field.dropdownOptions && field.dropdownOptions.length > 0) {
        this.dropdownItemsMap.push({
          dropdownOptions: field.dropdownOptions,
          value: field.userAttribute,
        });
      }
    }
  },
};
</script>

<style scoped>
.sign-up-manager-container {
  display: flex;
  flex-direction: column;
  padding: 1rem;
}

.v-divider {
  padding-bottom: 24px;
}

.v-input {
  margin: 0px;
}

.v-input ::v-deep .v-input--selection-controls__input {
  align-self: flex-start;
}

.required-fields {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding-bottom: 1rem;
}

.required-fields-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding-left: 0.75rem;
}

.additional-fields {
  display: flex;
  flex-direction: column;
}

.additional-fields-list-item {
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  align-items: center;
  justify-content: center;
  max-width: 500px;
  padding-bottom: 1.5rem;
}

.trash-button-padding-bottom {
  padding-bottom: 1.9rem !important;
}
</style>
